<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { Button, DashLayout, Text } from '@Shelly/shelly-design-system';
	import DashListHolderNew from 'src/components/Lists/Dashboards/DashListHolderNew.svelte';
	import ListSkeletonGrid from 'src/components/Lists/ListSkeletonGrid.svelte';
	import { devices } from 'src/stores/lists/devices';
	import { forceListSkeleton } from 'src/stores/features/dev/force-list-skeleton';
	import {
		dashLightingPackId,
		cycleDashLightingPack,
	} from 'src/stores/features/dev/dash-lighting-pack';
	import { DASH_LIGHTING_PACK_LABELS } from 'src/helpers/dash-lighting';
	import {
		ALL_DEVICE_WIDGETS_DASHBOARD_ID,
		buildAllDeviceWidgetsDashboard,
	} from './all-device-widgets';

	/** Short defer so HomeBackground can start its cross-fade before 2×N cards mount. */
	const WIDGET_MOUNT_DEFER_MS = 50;
	const SKELETON_CARD_COUNT = 8;

	const dashboardOverride = $derived(buildAllDeviceWidgetsDashboard($devices));

	let widgetsReady = $state(false);
	let deferTimer: ReturnType<typeof setTimeout> | null = null;
	let deferRaf1 = 0;
	let deferRaf2 = 0;

	onMount(() => {
		deferRaf1 = requestAnimationFrame(() => {
			deferRaf1 = 0;
			deferRaf2 = requestAnimationFrame(() => {
				deferRaf2 = 0;
				deferTimer = setTimeout(() => {
					deferTimer = null;
					widgetsReady = true;
				}, WIDGET_MOUNT_DEFER_MS);
			});
		});
	});

	onDestroy(() => {
		if (deferRaf1) cancelAnimationFrame(deferRaf1);
		if (deferRaf2) cancelAnimationFrame(deferRaf2);
		if (deferTimer) clearTimeout(deferTimer);
	});
</script>

<div class="all-device-widgets-pack-overlay" role="region" aria-label="Card feedback pack">
	<span class="all-device-widgets-pack-overlay__label">
		<Text styleType="body-small">
			Pack: <b>{DASH_LIGHTING_PACK_LABELS[$dashLightingPackId]}</b>
		</Text>
	</span>
	<Button variant="secondary" icon="fas fa-lightbulb" on:click={() => cycleDashLightingPack()}>
		Next pack
	</Button>
</div>

{#if widgetsReady && !$forceListSkeleton}
	<DashListHolderNew
		id={ALL_DEVICE_WIDGETS_DASHBOARD_ID}
		{dashboardOverride}
		allowAddWidgets={false}
		allowManageWidgets={false}
		allowReorderWidgets={false}
	/>
{:else}
	<div
		class="all-device-widgets-skeleton"
		aria-busy="true"
		aria-live="polite"
		aria-label="Loading device widgets"
	>
		<DashLayout>
			<svelte:fragment slot="leftContent">
				<ListSkeletonGrid
					count={SKELETON_CARD_COUNT}
					variant="device-widget"
					sm={100}
					lg={160}
					xl={160}
					gridRowMinContent
					disableGridMaxHeight
				/>
			</svelte:fragment>
			<svelte:fragment slot="rightContent">
				<ListSkeletonGrid
					count={SKELETON_CARD_COUNT}
					variant="control-widget"
					sm={100}
					lg={160}
					xl={160}
					gridRowMinContent
					disableGridMaxHeight
				/>
			</svelte:fragment>
		</DashLayout>
	</div>
{/if}

<style>
	.all-device-widgets-pack-overlay {
		position: fixed;
		z-index: 40;
		right: max(16px, env(safe-area-inset-right, 0px));
		bottom: max(88px, calc(env(safe-area-inset-bottom, 0px) + 72px));
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 8px;
		pointer-events: none;
	}

	.all-device-widgets-pack-overlay :global(button) {
		pointer-events: auto;
		box-shadow: 0 8px 24px color-mix(in oklab, var(--color-1) 35%, transparent);
	}

	.all-device-widgets-pack-overlay__label {
		padding: 6px 10px;
		border-radius: 8px;
		background: color-mix(in oklab, var(--color-1) 78%, transparent);
		backdrop-filter: blur(8px);
		-webkit-backdrop-filter: blur(8px);
		pointer-events: none;
	}
</style>
